"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { TwosViews } from "@/components/tools/cs-tools/twos-views";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { groupNibbles, signedDecimal } from "@/lib/tool-kit/cs-tools/bits";
import {
  evaluateTwos,
  type TwosFormat,
} from "@/lib/tool-kit/cs-tools/twos-eval";

/** English strings. A translated content file overrides these through `ui`. */
export const TWOS_COMPLEMENT_CALCULATOR_STRINGS = {
  formatLabel: "What are you entering?",
  formatDecimal: "A signed decimal number",
  formatBinary: "A binary bit pattern",
  formatHex: "A hexadecimal bit pattern",
  widthLabel: "Width in bits (4 to 64)",
  widthPresets: "Common widths",
  presetAria: "Use {bits} bits",
  decimalLabel: "Decimal number",
  decimalHint:
    "A whole number, positive or negative, of any size. Spaces and underscores are ignored.",
  decimalDefault: "-43",
  binaryLabel: "Binary pattern",
  binaryHint:
    "Only 0 and 1. A pattern shorter than the width is padded with zeros on the left.",
  binaryDefault: "11010101",
  hexLabel: "Hexadecimal pattern",
  hexHint:
    "Digits 0–9 and A–F, with an optional 0x. A pattern shorter than the width is padded with zeros on the left.",
  hexDefault: "D5",
  errWidth: "Enter a width that is a whole number from 4 to 64.",
  errEmpty: "Enter a value to convert.",
  errInvalidDecimal:
    "That is not a whole number. Use digits with an optional minus sign — fractions belong in the IEEE 754 converter.",
  errInvalidBinary: "A binary pattern can contain only the digits 0 and 1.",
  errInvalidHex:
    "A hexadecimal pattern can contain only the digits 0–9 and the letters A–F.",
  errTooWideBinary:
    "That pattern is longer than {width} bits. Raise the width or shorten the pattern.",
  errTooWideHex:
    "That pattern needs more than {width} bits. Raise the width or shorten the pattern.",
  resultsHeading: "Result",
  statBits: "Two's complement bits",
  statHex: "Hexadecimal",
  statSigned: "Signed value",
  statUnsigned: "Same bits read as unsigned",
  statRange: "Range at this width",
  statNeeded: "Smallest width that fits",
  statPatterns: "Distinct patterns",
  bitCount: "one: {count} bit|other: {count} bits",
  copyBits: "Copy bits",
  copyDecimal: "Copy signed value",
  copied: "Copied!",
  overflowTitle: "Overflow: this value does not fit",
  overflowBody:
    "{value} is outside the {width}-bit range of {min} to {max}. Hardware would keep only the low {width} bits, which read back as {stored}. The smallest width that holds {value} is {needed}.",
  rangeValue: "{min} to {max}",
  workingHeading: "How the bits were found",
  stepNonNegative:
    "The value is not negative, so its two's complement is just its binary form, padded with leading zeros to the width. The sign bit is 0.",
  stepWrapped:
    "The value wraps modulo {power}: only the low {width} bits are kept, and they read back as {stored}.",
  stepNonNegativeRead:
    "The sign bit is 0, so the pattern reads as ordinary binary: {value}.",
  stepMagnitude:
    "Write the size of the number (without its sign) in {width} bits",
  stepInvert: "Flip every bit (this is the one's complement)",
  stepAddOne: "Add 1 — the result is the two's complement",
  stepPatternFirst: "Start from the stored pattern",
  stepInvertBack: "Flip every bit",
  stepAddOneBack: "Add 1 — this gives the size of the number",
  stepSizeIs:
    "That is {magnitude}, and the sign bit was 1, so the value is −{magnitude}.",
  stepShortcut:
    "Shortcut: {power} − {magnitude} = {pattern}, where {power} is {total}.",
  weightsHeading: "Place values",
  weightsBody:
    "The sign bit counts as −{top}; every other 1 bit adds its power of two.",
  weightsSum: "{terms} = {value}",
  weightsNone: "No bits are set, so the value is 0.",
  formsHeading: "The same value in three signed formats",
  formName: "Format",
  formBits: "Bits",
  formSignMagnitude: "Sign and magnitude",
  formOnes: "One's complement",
  formTwos: "Two's complement",
  formNone: "No pattern for this value",
  extendHeading: "Sign extension",
  extendBody:
    "To keep the same value in {width} bits, copy the sign bit into the new top bits:",
  tableCaption: "Signed formats compared",
  stepsCaption: "Invert-and-add-one working",
  stepColumn: "Step",
  rowColumn: "Bits",
} as const;

type Strings = typeof TWOS_COMPLEMENT_CALCULATOR_STRINGS;

const PRESETS = [4, 8, 16, 32, 64] as const;

const LABELS: Record<TwosFormat, keyof Strings> = {
  decimal: "decimalLabel",
  binary: "binaryLabel",
  hex: "hexLabel",
};

const HINTS: Record<TwosFormat, keyof Strings> = {
  decimal: "decimalHint",
  binary: "binaryHint",
  hex: "hexHint",
};

export default function TwosComplementCalculatorWidget() {
  const strings = useToolUi(TWOS_COMPLEMENT_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [format, setFormat] = useState<TwosFormat>("decimal");
  const [widthText, setWidthText] = useState("8");
  const [values, setValues] = useState<Record<TwosFormat, string>>({
    decimal: strings.decimalDefault,
    binary: strings.binaryDefault,
    hex: strings.hexDefault,
  });

  const result = evaluateTwos(format, values[format], widthText);
  const analysis = result.kind === "ok" ? result.analysis : null;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.formatLabel}
        name="twos-format"
        onChange={setFormat}
        options={[
          { value: "decimal", label: strings.formatDecimal },
          { value: "binary", label: strings.formatBinary },
          { value: "hex", label: strings.formatHex },
        ]}
        value={format}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings[HINTS[format]]}
          id="twos-value"
          label={strings[LABELS[format]]}
          mono
          onChange={(next) => setValues({ ...values, [format]: next })}
          value={values[format]}
        />
        <div className="space-y-2">
          <TextField
            id="twos-width"
            label={strings.widthLabel}
            numeric
            onChange={setWidthText}
            value={widthText}
          />
          <fieldset className="flex flex-wrap items-center gap-2">
            <legend className={`${LABEL} sr-only`}>
              {strings.widthPresets}
            </legend>
            {PRESETS.map((bits) => (
              <button
                aria-label={t(strings.presetAria, { bits })}
                className={BUTTON_SMALL}
                key={bits}
                onClick={() => setWidthText(String(bits))}
                type="button"
              >
                {fmt(bits)}
              </button>
            ))}
          </fieldset>
        </div>
      </div>

      <div aria-live="polite" className="space-y-5">
        {result.kind === "error" ? (
          <ErrorNotice>
            {t(strings[result.key], { width: widthText.trim() })}
          </ErrorNotice>
        ) : null}
        {analysis ? (
          <>
            <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
            {analysis.fits ? null : (
              <Notice>
                <strong>{strings.overflowTitle}</strong>{" "}
                {t(strings.overflowBody, {
                  value: signedDecimal(analysis.entered),
                  width: fmt(analysis.width),
                  min: signedDecimal(analysis.range.min),
                  max: signedDecimal(analysis.range.max),
                  stored: signedDecimal(analysis.signed),
                  needed: tPlural(
                    strings.bitCount,
                    locale,
                    analysis.neededWidth,
                    {
                      count: fmt(analysis.neededWidth),
                    }
                  ).trim(),
                })}
              </Notice>
            )}
            <StatGrid>
              <StatCard
                emphasis
                label={strings.statBits}
                value={groupNibbles(analysis.bits)}
              />
              <StatCard label={strings.statHex} value={analysis.hex} />
              <StatCard
                label={strings.statSigned}
                value={signedDecimal(analysis.signed)}
              />
              <StatCard
                label={strings.statUnsigned}
                value={analysis.unsigned.toString()}
              />
              <StatCard
                label={strings.statRange}
                value={t(strings.rangeValue, {
                  min: signedDecimal(analysis.range.min),
                  max: signedDecimal(analysis.range.max),
                })}
              />
              <StatCard
                label={strings.statNeeded}
                value={tPlural(strings.bitCount, locale, analysis.neededWidth, {
                  count: fmt(analysis.neededWidth),
                }).trim()}
              />
            </StatGrid>
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyBits}
                text={analysis.bits}
              />
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyDecimal}
                text={analysis.signed.toString()}
              />
            </div>
            <TwosViews analysis={analysis} format={format} strings={strings} />
          </>
        ) : null}
      </div>
    </div>
  );
}
